import { useState } from 'react'
import { useMutation } from '@truepill/tpos-react-router'
import { ReactComponent as LoadingDots } from 'assets/icons/loading_dots.svg'
import { ReactComponent as RouteIcon } from 'assets/icons/route.svg'
import ActionButton from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { BULK_SEND_DEFERRED_TO_AUTOMATION, BULK_SEND_TO_AUTOMATION } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import { bodyPrimaryColor } from 'styles/styleVariables'

type SendToAutomationFill = {
  fillId: string
  orderId: string
  name?: string
}

type BulkSendToAutomationResult = {
  fillId: string
  result?: string | null
  error?: string | null
}

type SendToAutomationButtonProps = {
  fills: SendToAutomationFill[]
  deferred?: boolean
  allowBatch?: boolean
  disabled?: boolean
  onCompleted?: () => void
}

const MAX_FILLS_PER_REQUEST = 100

const SendToAutomationButton = ({
  fills,
  deferred = false,
  allowBatch = false,
  disabled = false,
  onCompleted,
}: SendToAutomationButtonProps): JSX.Element => {
  const showErrorToast = useErrorToast()
  const showSuccessToast = useSuccessToast()
  const [loading, setLoading] = useState(false)
  const [bulkSendDeferredToAutomation] = useMutation(BULK_SEND_DEFERRED_TO_AUTOMATION)
  const [bulkSendToAutomation] = useMutation<{ bulkSendToAutomation: BulkSendToAutomationResult[] }>(
    BULK_SEND_TO_AUTOMATION,
  )

  const sendToAutomation = async () => {
    const fillInputs = fills.map(({ fillId, orderId }) => ({ fillId, orderId }))
    setLoading(true)
    try {
      if (deferred) {
        const res = await bulkSendDeferredToAutomation({ variables: { fills: fillInputs } })
        const errors = (res.data?.bulkSendDeferredToAutomation ?? [])
          .map((i: { error?: string }) => i.error)
          .filter(Boolean)
        if (errors.length) {
          showErrorToast(errors.join('/'))
        } else {
          showSuccessToast(`Successfully sent to automation.`)
        }
      } else {
        const results: BulkSendToAutomationResult[] = []
        for (let i = 0; i < fillInputs.length; i += MAX_FILLS_PER_REQUEST) {
          const chunk = fillInputs.slice(i, i + MAX_FILLS_PER_REQUEST)
          const res = await bulkSendToAutomation({ variables: { fills: chunk, allowBatch } })
          results.push(...(res.data?.bulkSendToAutomation ?? []))
        }
        const drugByFill = new Map(fills.map(({ fillId, name }) => [fillId, name]))
        const failed = results.filter(({ error }) => error)
        if (failed.length) {
          // group affected drugs under each (deduped) reason
          const drugsByReason = new Map<string, string[]>()
          failed.forEach(({ fillId, error }) => {
            const reason = error ?? ''
            const drugs = drugsByReason.get(reason) ?? []
            drugs.push(drugByFill.get(fillId) ?? fillId)
            drugsByReason.set(reason, drugs)
          })
          const details = [...drugsByReason]
            .map(([reason, drugs]) => {
              // show only the first few fill drug names
              const shown = drugs.slice(0, 3).join(', ')
              const extra = drugs.length > 3 ? ` +${drugs.length - 3} more` : ''
              return `${reason} (${drugs.length}): ${shown}${extra}`
            })
            .join('. ')
          showErrorToast(`${failed.length} of ${results.length} fills failed: ${details}`)
        } else {
          showSuccessToast(`Successfully sent to automation.`)
        }
      }
    } catch (error) {
      showErrorToast(`Failed to send to automation: ${error.message}`)
    } finally {
      setLoading(false)
      onCompleted?.()
    }
  }

  return (
    <ActionButton
      icon={
        loading ? (
          <IconWrapper>
            <LoadingDots fill={bodyPrimaryColor} />
          </IconWrapper>
        ) : (
          <IconWrapper>
            <RouteIcon fill={bodyPrimaryColor} />
          </IconWrapper>
        )
      }
      label={'Send to Automation'}
      disabled={disabled || !fills.length || loading}
      onClick={() => void sendToAutomation()}
    />
  )
}

export default SendToAutomationButton
